import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Chips, DateField, usePMeta } from "../../components/site/Chips";
import { inputCls, btnMain, btnLine } from "./ReaderShell";

export const EMPTY_PROFILE = { nationalities: [], nationality_undisclosed: false, residence: null, residence_valid_from: "", residence_valid_until: "", residence_note: "", state: null, city: "", plz_region: "", interests: [], situations: [], preferred_categories: [], hidden_categories: [], personalization_enabled: true };

const Why = ({ children }) => <p className="mb-4 text-[13.5px] text-muted-foreground">{children}</p>;

const STEPS = [
  ["Državljanstvo", ({ p, set, m }) => (
    <>
      <Why>Koristimo samo za vijesti koje redakcija označi kao važne za određeno državljanstvo (npr. vize, dvojno državljanstvo). Možeš odabrati više ili preskočiti.</Why>
      <Chips options={m.nationalities} value={p.nationalities} onChange={(v) => set({ nationalities: v, nationality_undisclosed: false })} testId="wiz-nat" />
      <label className="mt-4 flex items-center gap-2 text-sm"><input type="checkbox" checked={p.nationality_undisclosed} onChange={(e) => set({ nationality_undisclosed: e.target.checked, nationalities: [] })} data-testid="wiz-nat-undisclosed" /> Ne želim reći</label>
    </>
  )],
  ["Boravišni status", ({ p, set, m, account }) => (
    <>
      <Why>Pomaže da ti prikažemo promjene propisa za tvoju vrstu boravka. Ovo nije pravna provjera – paragraf sam po sebi ne određuje sva tvoja prava.</Why>
      <Chips options={m.residence} value={p.residence} multi={false} onChange={(v) => set({ residence: v })} testId="wiz-res" />
      {account && p.residence && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div><label htmlFor="w-rf" className="mb-1 block text-xs font-semibold">Važi od (opcionalno)</label><DateField id="w-rf" value={p.residence_valid_from} onChange={(v) => set({ residence_valid_from: v })} className={inputCls} testId="wiz-res-from" /></div>
          <div><label htmlFor="w-ru" className="mb-1 block text-xs font-semibold">Važi do (opcionalno)</label><DateField id="w-ru" value={p.residence_valid_until} onChange={(v) => set({ residence_valid_until: v })} className={inputCls} testId="wiz-res-until" /></div>
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">Ništa ne odabereš = „Nepoznato / ne želim reći“.</p>
    </>
  )],
  ["Gdje živiš", ({ p, set, m }) => (
    <>
      <Why>Savezna pokrajina je dovoljna za regionalne vijesti. Nikad ne tražimo tačnu adresu.</Why>
      <select value={p.state || ""} onChange={(e) => set({ state: e.target.value || null })} className={inputCls} aria-label="Savezna pokrajina" data-testid="wiz-state-select">
        <option value="">— ne želim navesti —</option>{Object.entries(m.states).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_140px]">
        <input placeholder="Grad (opcionalno)" value={p.city} maxLength={80} onChange={(e) => set({ city: e.target.value })} className={inputCls} data-testid="wiz-city-input" />
        <input placeholder="PLZ – prve 2 cifre" inputMode="numeric" value={p.plz_region} maxLength={2} onChange={(e) => set({ plz_region: e.target.value.replace(/\D/g, "") })} className={inputCls} data-testid="wiz-plz-input" />
      </div>
    </>
  )],
  ["Teme koje pratiš", ({ p, set, m }) => (
    <><Why>Odaberi teme o kojima želiš više vijesti.</Why><Chips options={m.interests} value={p.interests} onChange={(v) => set({ interests: v })} testId="wiz-int" /></>
  )],
  ["Porodica i posao", ({ p, set, m }) => (
    <><Why>Samo opće oznake – ne tražimo imena ni datume rođenja djece.</Why><Chips options={m.situations} value={p.situations} onChange={(v) => set({ situations: v })} testId="wiz-sit" /></>
  )],
];

export function ProfileWizard({ initial, onSave, account = false, saveLabel = "Spremi i prikaži moje vijesti" }) {
  const m = usePMeta();
  const [p, setP] = useState({ ...EMPTY_PROFILE, ...(initial || {}) });
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  if (!m) return null;
  const set = (patch) => setP((x) => ({ ...x, ...patch }));
  const [title, Body] = STEPS[step];
  const last = step === STEPS.length - 1;
  const save = async () => {
    setBusy(true);
    try {
      await onSave(p);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="rounded-sm border border-border bg-card p-5 sm:p-8" data-testid="profile-wizard">
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground" data-testid="wizard-step-indicator">Korak {step + 1} od {STEPS.length}</p>
        <div className="flex h-1.5 flex-1 max-w-[200px] overflow-hidden rounded-full bg-muted"><div className="bg-pulse transition-[width] duration-300" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} /></div>
      </div>
      <h2 className="hl mt-4 text-[1.35rem]" data-testid="wizard-step-title">{title}</h2>
      <div className="mt-3"><Body p={p} set={set} m={m} account={account} /></div>
      <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
        <button type="button" disabled={step === 0} onClick={() => setStep(step - 1)} className={btnLine} data-testid="wizard-back"><ChevronLeft className="h-4 w-4" /> Nazad</button>
        <div className="flex flex-wrap gap-2">
          {!last && <button type="button" onClick={() => setStep(step + 1)} className={btnLine} data-testid="wizard-skip">Preskoči</button>}
          {!last && <button type="button" onClick={() => setStep(step + 1)} className={btnMain} data-testid="wizard-next">Dalje <ChevronRight className="h-4 w-4" /></button>}
          <button type="button" disabled={busy} onClick={save} className={last ? btnMain : btnLine} data-testid="wizard-save">{busy ? "Spremam…" : saveLabel}</button>
        </div>
      </div>
    </section>
  );
}
